import { getCookie } from "cookies-next";
import merge from "lodash/merge";

import { BookingScheduleDay } from "@/lib/types/schedule-types";
import { normalizeListingObj } from "src/app/analytics/normalize-listing";
import { ListingData } from "~/context/ListingPageDataContext";
import { BookListingQuote, IFaqs } from "~/types/api-types";
import { NormalizedProfile } from "~/types/profile.types";
import { normalizePriceRange } from "~/utils/normalizePriceRange";

const handleBookingPriceCreatedAnalytics = (
  quote: BookListingQuote,
  filteredSchedule: BookingScheduleDay[],
  listingData: ListingData,
) => {
  const { spaceRentalContract, masterContract } = quote || {};
  const { booking } = spaceRentalContract?.meta || {};

  const editedFeeObj = window.analyticsIntegrations?.parseFeeObj(booking);
  const bookingTotal = masterContract?.calculations?.guest?.payment || null;
  const baseListingPrice = listingData.listingPrice;
  const {
    type: priceType,
    isRange,
    isTotal,
    isHourlyRate,
  } = normalizePriceRange(
    listingData.listingPriceRange || {},
    booking?.rate ?? null,
    bookingTotal,
  );
  const priceQuoted = isRange
    ? listingData.listingPriceRange
    : isTotal
      ? bookingTotal
      : isHourlyRate
        ? booking?.rate
        : baseListingPrice;

  const properties = merge(
    editedFeeObj,
    normalizeListingObj(listingData),
    { optimize_experiments: {} },
    { day_count: filteredSchedule?.length },
    { rate_details: booking?.rate_details },
    {
      split_payment: masterContract?.calculations?.guest?.split_payment,
      split_payment_permitted: masterContract?.split_payment_permitted || false,
    },
    {
      page: "listing",
      price_type: priceType,
      price_quoted: priceQuoted,
      currency: listingData.listingCurrency,
    },
    {
      search_keyword: getCookie("PSSK") !== undefined ? getCookie("PSSK") : "",
      search_keyword_type:
        getCookie("PSSKT") !== undefined ? getCookie("PSSKT") : "",
    },
  );

  window.analyticsIntegrations.bookingPriceCreated(
    properties,
    [masterContract!, spaceRentalContract!],
    listingData.useType,
    listingData.metro,
    !!listingData.fullService?.active,
  );
};

const handleStartBookingAnalytics = ({
  listingData,
  profile,
  faqs,
  pageTitle,
  rateDetails,
  userMessagesCount,
}: {
  listingData: ListingData;
  profile: NormalizedProfile | undefined;
  faqs: IFaqs | undefined;
  pageTitle: string;
  rateDetails?: Record<string, unknown>;
  userMessagesCount?: number;
}) => {
  const expandedListing = {
    ...normalizeListingObj(listingData),
    neighborhood: listingData.neighborhood,
    event_details: faqs,
    page_title: pageTitle,
    city: listingData.listingCity,
    state: listingData.listingState,
    country: listingData.listingCountry,
    zip: listingData.zipGoogle,
    latitude: listingData.latitude,
    longitude: listingData.longitude,
    ownerFirstName: listingData.listingOwnerFirstName,
    ownerLastName: listingData.ownerLastName,
    ownerAvatarUrl: listingData.ownerAvatar,
    ownerAvatarId: listingData.ownerAvatarId,
    prices: listingData.prices,
    host_responsiveness: listingData.listingHostResponsiveness,
    parking_info: listingData.parkingDetails,
    canonical_loc_data: listingData.canonicalLocData,
    currency: listingData.listingCurrency,
    images: listingData.images,
    cover_img: listingData.coverImg,
    user_info: {
      avatar_url: profile?.avatarUrl,
      is_host: profile?.isHost,
      SSOid: profile?.SSOid,
      ga_client_id: profile?.gaClientId,
      profession: profile?.profession,
      organization: profile?.organization,
      message_count: userMessagesCount,
    },
  };
  window.analyticsIntegrations.startBooking(
    expandedListing,
    "listing",
    rateDetails ? { rate_details: rateDetails } : {},
  );
};

export { handleBookingPriceCreatedAnalytics, handleStartBookingAnalytics };
